/**
 * ProjectService.ts
 * Service de gestion des projets avec Supabase
 * Remplace les appels HTTP vers le backend Express par des requêtes directes à Supabase
 */
import { supabase, isSupabaseConfigured } from './SupabaseService';
import type { Database } from '../types/supabase';
import { logger } from '../utils/ProfessionalLogger';
import type { AppState } from './StateService';

// Types pour les projets
export type ProjectRow = Database['public']['Tables']['template_editor_projects']['Row'];
export type ProjectInsert = Database['public']['Tables']['template_editor_projects']['Insert'];
export type ProjectUpdate = Database['public']['Tables']['template_editor_projects']['Update'];

/**
 * Interface pour un projet complet (incluant les données canvas)
 */
export interface Project {
  id: string;
  user_id: string;
  name: string;
  canvas_data: {
    shapes: AppState['shapes'];
    canvas: AppState['canvas'];
    project: AppState['project'];
    selection?: AppState['selection'];
  };
  thumbnail_url: string | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Options pour la création d'un projet
 */
export interface CreateProjectOptions {
  name: string;
  canvas_data: Project['canvas_data'];
  thumbnail_url?: string | null;
  is_public?: boolean;
}

/**
 * Options pour la mise à jour d'un projet
 */
export interface UpdateProjectOptions {
  name?: string;
  canvas_data?: Project['canvas_data'];
  thumbnail_url?: string | null;
  is_public?: boolean;
}

/**
 * Résultat d'une opération sur un projet
 */
export interface ProjectOperationResult<T = Project> {
  data: T | null;
  error: Error | null;
}

/**
 * Service de gestion des projets avec Supabase
 */
export class ProjectService {
  /**
   * Récupère tous les projets de l'utilisateur connecté
   */
  async getAllProjects(): Promise<ProjectOperationResult<Project[]>> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = new Error("Supabase n'est pas configuré");
      logger.warn('Tentative de récupération des projets sans configuration Supabase');
      return { data: null, error };
    }

    try {
      const { data, error } = await supabase
        .from('template_editor_projects')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) {
        logger.error('Erreur lors de la récupération des projets:', error);
        return { data: null, error: new Error(error.message) };
      }

      logger.info(`✅ ${data?.length || 0} projet(s) récupéré(s)`);
      return { data: data as unknown as Project[], error: null };
    } catch (error) {
      logger.error('Exception lors de la récupération des projets:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Récupère un projet par son ID
   */
  async getProjectById(projectId: string): Promise<ProjectOperationResult<Project>> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = new Error("Supabase n'est pas configuré");
      logger.warn("Tentative de récupération d'un projet sans configuration Supabase");
      return { data: null, error };
    }

    try {
      const { data, error } = await supabase
        .from('template_editor_projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          logger.warn(`Projet ${projectId} non trouvé`);
          return { data: null, error: new Error('Projet non trouvé') };
        }
        logger.error('Erreur lors de la récupération du projet:', error);
        return { data: null, error: new Error(error.message) };
      }

      logger.info(`✅ Projet ${projectId} récupéré`);
      return { data: data as unknown as Project, error: null };
    } catch (error) {
      logger.error('Exception lors de la récupération du projet:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Crée un nouveau projet
   */
  async createProject(options: CreateProjectOptions): Promise<ProjectOperationResult<Project>> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = new Error("Supabase n'est pas configuré");
      logger.warn('Tentative de création de projet sans configuration Supabase');
      return { data: null, error };
    }

    try {
      // Récupérer l'utilisateur actuel
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        logger.error("Impossible de récupérer l'utilisateur actuel:", userError);
        return { data: null, error: new Error('Utilisateur non authentifié') };
      }

      const projectData: ProjectInsert = {
        user_id: user.id,
        name: options.name,
        canvas_data: options.canvas_data as unknown as import('../types/supabase').Json,
        thumbnail_url: options.thumbnail_url ?? null,
        is_public: options.is_public ?? false,
      };

      const { data, error } = await supabase
        .from('template_editor_projects')
        .insert(projectData)
        .select()
        .single();

      if (error) {
        logger.error('Erreur lors de la création du projet:', error);
        return { data: null, error: new Error(error.message) };
      }

      if (!data) {
        return { data: null, error: new Error('Aucune donnée retournée') };
      }
      logger.info(`✅ Projet créé: ${data.id} (${data.name})`);
      return { data: data as unknown as Project, error: null };
    } catch (error) {
      logger.error('Exception lors de la création du projet:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Met à jour un projet existant
   * PR-07 : Optimistic concurrency - vérifie updated_at pour détecter les conflits
   */
  async updateProject(
    projectId: string,
    options: UpdateProjectOptions & { expectedUpdatedAt?: string }
  ): Promise<ProjectOperationResult<Project & { conflictDetected?: boolean }>> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = new Error("Supabase n'est pas configuré");
      logger.warn('Tentative de mise à jour de projet sans configuration Supabase');
      return { data: null, error };
    }

    try {
      const updateData: ProjectUpdate = {};

      if (options.name !== undefined) updateData.name = options.name;
      if (options.canvas_data !== undefined)
        updateData.canvas_data = options.canvas_data as unknown as import('../types/supabase').Json;
      if (options.thumbnail_url !== undefined) updateData.thumbnail_url = options.thumbnail_url;
      if (options.is_public !== undefined) updateData.is_public = options.is_public;

      // PR-07 : Optimistic concurrency - vérifier updated_at si fourni
      let query = supabase
        .from('template_editor_projects')
        .update(updateData as Record<string, unknown>)
        .eq('id', projectId);

      if (options.expectedUpdatedAt) {
        query = query.eq('updated_at', options.expectedUpdatedAt);
      }

      const { data, error } = await query.select().single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Aucune ligne mise à jour = conflit détecté (si expectedUpdatedAt était fourni)
          if (options.expectedUpdatedAt) {
            logger.warn(
              `Conflit détecté pour projet ${projectId} - version plus récente sur le serveur`
            );
            return {
              data: null,
              error: new Error('CONFLICT_DETECTED'),
            };
          }
          logger.warn(`Projet ${projectId} non trouvé pour mise à jour`);
          return { data: null, error: new Error('Projet non trouvé') };
        }
        logger.error('Erreur lors de la mise à jour du projet:', error);
        return { data: null, error: new Error(error.message) };
      }

      if (!data) {
        // Si expectedUpdatedAt était fourni et aucune ligne mise à jour, c'est un conflit
        if (options.expectedUpdatedAt) {
          return {
            data: null,
            error: new Error('CONFLICT_DETECTED'),
          };
        }
        return { data: null, error: new Error('Aucune donnée retournée') };
      }
      logger.info(`✅ Projet mis à jour: ${projectId} (${data.name})`);
      return { data: data as unknown as Project, error: null };
    } catch (error) {
      logger.error('Exception lors de la mise à jour du projet:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Supprime un projet
   */
  async deleteProject(projectId: string): Promise<ProjectOperationResult<void>> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = new Error("Supabase n'est pas configuré");
      logger.warn('Tentative de suppression de projet sans configuration Supabase');
      return { data: null, error };
    }

    try {
      const { error } = await supabase
        .from('template_editor_projects')
        .delete()
        .eq('id', projectId);

      if (error) {
        logger.error('Erreur lors de la suppression du projet:', error);
        return { data: null, error: new Error(error.message) };
      }

      logger.info(`✅ Projet supprimé: ${projectId}`);
      return { data: null, error: null };
    } catch (error) {
      logger.error('Exception lors de la suppression du projet:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Récupère les projets publics (galerie)
   */
  async getPublicProjects(limit: number = 50): Promise<ProjectOperationResult<Project[]>> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = new Error("Supabase n'est pas configuré");
      logger.warn('Tentative de récupération des projets publics sans configuration Supabase');
      return { data: null, error };
    }

    try {
      const { data, error } = await supabase
        .from('template_editor_projects')
        .select('*')
        .eq('is_public', true)
        .order('updated_at', { ascending: false })
        .limit(limit);

      if (error) {
        logger.error('Erreur lors de la récupération des projets publics:', error);
        return { data: null, error: new Error(error.message) };
      }

      logger.info(`✅ ${data?.length || 0} projet(s) public(s) récupéré(s)`);
      return { data: data as unknown as Project[], error: null };
    } catch (error) {
      logger.error('Exception lors de la récupération des projets publics:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Duplique un projet
   */
  async duplicateProject(
    projectId: string,
    newName?: string
  ): Promise<ProjectOperationResult<Project>> {
    // Récupérer le projet source
    const { data: sourceProject, error: fetchError } = await this.getProjectById(projectId);

    if (fetchError || !sourceProject) {
      return { data: null, error: fetchError || new Error('Projet source non trouvé') };
    }

    // Créer une copie avec un nouveau nom
    const duplicateName = newName || `${sourceProject.name} (Copie)`;
    return this.createProject({
      name: duplicateName,
      canvas_data: sourceProject.canvas_data,
      thumbnail_url: sourceProject.thumbnail_url,
      is_public: false, // Les copies sont privées par défaut
    });
  }

  /**
   * Recherche des projets par nom
   */
  async searchProjects(query: string): Promise<ProjectOperationResult<Project[]>> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = new Error("Supabase n'est pas configuré");
      logger.warn('Tentative de recherche de projets sans configuration Supabase');
      return { data: null, error };
    }

    try {
      const { data, error } = await supabase
        .from('template_editor_projects')
        .select('*')
        .ilike('name', `%${query}%`)
        .order('updated_at', { ascending: false });

      if (error) {
        logger.error('Erreur lors de la recherche de projets:', error);
        return { data: null, error: new Error(error.message) };
      }

      logger.info(`✅ ${data?.length || 0} projet(s) trouvé(s) pour "${query}"`);
      return { data: data as unknown as Project[], error: null };
    } catch (error) {
      logger.error('Exception lors de la recherche de projets:', error);
      return { data: null, error: error as Error };
    }
  }
}

// Instance singleton
export const projectService = new ProjectService();
